import { CloseThinIcon } from "@starrez/starryui";
import * as keycode from "keycode";
import * as React from "react";
import { createGlobalStyle } from "styled-components";
import { v1 as uuid } from "uuid";
import { IBaseModalProps } from ".";
import { strings } from "../../../localisation";
import { Article, CaptionDiv, CloseButton, ModalDiv, ModalHeader, Section } from "./BaseModal.styles";

const BodyStyle = createGlobalStyle`
        body {
            overflow: hidden;
        }
    `;

const BaseModal = ({
    BorderRadius = "6px",
    CloseButtonHoverColor,
    children,
    CloseAriaLabel = strings.Action_CloseEmailWizard,
    CloseTitle = strings.Action_CloseEmailWizard,
    DisableClose = false,
    Header,
    HeaderColor = "white",
    HeaderTextColor,
    Height,
    ListenToBackgroundClick = true,
    MarginTopBottom = "0px",
    Offset = { Top: "0", Left: "0" },
    OnCloseRequest,
    Width,
}: IBaseModalProps) => {
    const sectionRef = React.createRef<HTMLDivElement>();
    const idToUse = uuid();

    React.useEffect(() => {
        if (sectionRef.current) {
            sectionRef.current.focus();
        }
    }, []);

    const stopClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
    };

    const handleContainerClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (ListenToBackgroundClick && !DisableClose) {
            OnCloseRequest();
        }
    };

    const handleCloseClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (!DisableClose) {
            OnCloseRequest();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        e.stopPropagation();
        if (isEventKey(e, keycode.codes.esc) && !DisableClose) {
            OnCloseRequest();
        }
    };

    const isEventKey = (e: React.KeyboardEvent<HTMLElement>, keyCode: number): boolean => {
        if (keycode.isEventKey(e.nativeEvent, keyCode)) {
            e.preventDefault();
            return true;
        } else {
            return false;
        }
    };

    return (
        <ModalDiv onClick={handleContainerClick} Offset={Offset}>
            <BodyStyle />
            <Section
                ref={sectionRef}
                theme={{ Height, Width, Offset, MarginTopBottom, BorderRadius }}
                role="dialog"
                aria-labelledby={idToUse}
                onClick={stopClick}
                onKeyDown={handleKeyDown}
                tabIndex={0}
            >
                <ModalHeader
                    onKeyDown={handleKeyDown}
                    HeaderColor={HeaderColor}
                    HeaderTextColor={HeaderTextColor}
                    BorderRadius={BorderRadius}
                >
                    <CaptionDiv id={idToUse}>{Header}</CaptionDiv>

                    <CloseButton
                        type="button"
                        onClick={handleCloseClick}
                        onKeyDown={handleKeyDown}
                        title={CloseTitle}
                        aria-label={CloseAriaLabel}
                        ButtonColor={HeaderTextColor}
                        CloseButtonHoverColor={CloseButtonHoverColor}
                        disabled={DisableClose}
                    >
                        <CloseThinIcon Height="24px" Width="24px" />
                    </CloseButton>
                </ModalHeader>

                <Article>
                    {children}
                </Article>
            </Section>
        </ModalDiv>
    );
};

export default BaseModal;
